import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Descriptions, Modal, Space, Spin, Typography, message } from "antd";
import { DownloadOutlined, FilePdfOutlined } from "@ant-design/icons";

import {
    downloadTransactionPlanInvoice,
    viewTransactionPlanInvoice,
} from "../../api/transactionPlanApi";
import { saveBlob } from "../transactionSchedule/fileActions";
import { formatFileSize, formatPlanDateTime } from "./transactionPlanUi";
import { useTranslation } from "../../i18n/LanguageProvider";

const { Text } = Typography;

/**
 * Shows the invoice PDF of one Гүйлгээний төлөвлөгөө row inside a modal, without leaving the
 * list page, and offers the original file for download.
 */
function TransactionPlanInvoiceModal({ open, record, onClose }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState("");
    const [blobUrl, setBlobUrl] = useState("");

    const planId = record?.id;

    const loadInvoice = useCallback(async (id) => {
        setLoading(true);
        setError("");

        try {
            const result = await viewTransactionPlanInvoice(id);
            const url = window.URL.createObjectURL(result.blob);
            setBlobUrl(url);
        } catch (err) {
            setError(err?.message || t("invoice.loadFailed"));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        if (!open || !planId) return undefined;

        loadInvoice(planId);

        return undefined;
    }, [open, planId, loadInvoice]);

    // The object URL lives as long as the modal shows that file.
    useEffect(() => {
        if (!blobUrl) return undefined;

        return () => {
            window.URL.revokeObjectURL(blobUrl);
        };
    }, [blobUrl]);

    useEffect(() => {
        if (!open) {
            setBlobUrl("");
            setError("");
        }
    }, [open]);

    const handleDownload = async () => {
        if (!planId) return;

        setDownloading(true);

        try {
            const result = await downloadTransactionPlanInvoice(planId);
            saveBlob(result, record?.invoiceFileName || `invoice-${planId}.pdf`);
            message.success(t("invoice.downloaded"));
        } catch (err) {
            message.error(err?.message || t("invoice.downloadFailed"));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Modal
            title={
                <Space>
                    <FilePdfOutlined style={{ color: "#cf1322" }} />
                    <span>{t("invoice.modalTitle")}</span>
                </Space>
            }
            open={open}
            onCancel={onClose}
            width={960}
            destroyOnClose
            footer={
                <Space>
                    <Button onClick={onClose}>{t("action.close")}</Button>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        loading={downloading}
                        disabled={!record?.hasInvoice}
                        onClick={handleDownload}
                    >
                        {t("action.download")}
                    </Button>
                </Space>
            }
        >
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered>
                    <Descriptions.Item label={t("col.id")}>{record?.id ?? "-"}</Descriptions.Item>
                    <Descriptions.Item label={t("col.sender")}>{record?.sender || "-"}</Descriptions.Item>
                    <Descriptions.Item label={t("col.beneficiary")}>
                        {record?.beneficiary || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("invoice.file")}>
                        {record?.invoiceFileName || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("invoice.size")}>
                        {formatFileSize(record?.invoiceFileSize)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("invoice.uploaded")}>
                        {formatPlanDateTime(record?.invoiceUploadedAt)}
                        {record?.invoiceUploadedBy ? ` / ${record.invoiceUploadedBy}` : ""}
                    </Descriptions.Item>
                </Descriptions>

                {error && (
                    <Alert type="error" showIcon message={t("invoice.error")} description={error} />
                )}

                <div
                    style={{
                        border: "1px solid #f0f0f0",
                        borderRadius: 10,
                        background: "#fafafa",
                        height: 620,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                    }}
                >
                    {loading && <Spin tip={t("invoice.loading")} />}

                    {!loading && blobUrl && (
                        <iframe
                            title="transaction-plan-invoice"
                            src={blobUrl}
                            style={{ width: "100%", height: "100%", border: "none" }}
                        />
                    )}

                    {!loading && !blobUrl && !error && (
                        <Text type="secondary">{t("invoice.empty")}</Text>
                    )}
                </div>
            </Space>
        </Modal>
    );
}

export default TransactionPlanInvoiceModal;
